import React, { useState, useEffect } from 'react';
import { X, Mail, ArrowRight } from 'lucide-react';
import { subscribeEmail } from '../services/api';
import { useLanguage } from '../context/LanguageContext';
import { t } from '../translations';

const EmailPopup = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { language } = useLanguage();

  useEffect(() => {
    // Check if user has already subscribed (permanent)
    const hasSubscribed = localStorage.getItem('emailSubscribed');
    
    // Check if user dismissed popup recently (temporary - 24 hour cooldown)
    const dismissedAt = localStorage.getItem('emailPopupDismissedAt');
    const cooldownHours = 24; // Show again after 24 hours if not subscribed
    
    let shouldShowPopup = false;
    
    if (hasSubscribed) {
      // Never show to subscribers
      shouldShowPopup = false;
    } else if (dismissedAt) {
      // Check if cooldown has passed
      const dismissedTime = parseInt(dismissedAt, 10);
      const now = Date.now();
      const hoursPassed = (now - dismissedTime) / (1000 * 60 * 60);
      shouldShowPopup = hoursPassed >= cooldownHours;
    } else {
      // First time visitor - show popup
      shouldShowPopup = true;
    }

    if (shouldShowPopup) {
      // Show popup after 15 seconds
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 30000);

      return () => {
        clearTimeout(timer);
      };
    }
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    // Set cooldown - will show again after 24 hours
    localStorage.setItem('emailPopupDismissedAt', Date.now().toString());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    try {
      await subscribeEmail(email, phone || null, 'popup');
      setSubmitted(true);
      // Permanently mark as subscribed
      localStorage.setItem('emailSubscribed', 'true');
      // Clear any dismiss cooldown
      localStorage.removeItem('emailPopupDismissedAt');
      setTimeout(() => {
        setIsVisible(false);
      }, 2000);
    } catch (error) {
      console.error('Subscription error:', error);
      // Check if it's a duplicate email error
      if (error.response?.status === 400) {
        alert(language === 'en' ? 'This email is already subscribed!' : '¡Este correo ya está suscrito!');
        localStorage.setItem('emailSubscribed', 'true');
        setIsVisible(false);
      } else {
        alert(language === 'en' ? 'Failed to subscribe. Please try again.' : 'Error al suscribirse. Por favor intenta de nuevo.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 animate-fadeIn"
        onClick={handleClose}
        data-testid="email-popup-backdrop"
      />

      {/* Popup */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div
          className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 relative pointer-events-auto transform animate-scaleIn"
          data-testid="email-popup-modal"
        >
          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors"
            data-testid="email-popup-close"
          >
            <X size={20} className="text-gray-500" />
          </button>

          {submitted ? (
            <div className="text-center" data-testid="email-popup-success">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail size={32} className="text-green-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                {language === 'en' ? 'Thank You!' : '¡Gracias!'}
              </h3>
              <p className="text-gray-600">
                {language === 'en' 
                  ? "You're now part of our mission to rescue and restore children in Colombia."
                  : "Ahora eres parte de nuestra misión de rescatar y restaurar niños en Colombia."}
              </p>
            </div>
          ) : (
            <div>
              <div className="w-16 h-16 bg-gradient-to-br from-sh-primary to-sh-bright rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail size={32} className="text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-1 text-center">
                {language === 'en' ? 'Stay Connected.' : 'Mantente Conectado.'}
              </h3>
              <h3 className="text-2xl font-bold text-sh-primary mb-4 text-center">
                {language === 'en' ? 'Stay Inspired.' : 'Mantente Inspirado.'}
              </h3>
              <p className="text-gray-600 mb-6 text-center">
                {language === 'en' 
                  ? 'Newsletter with updates, stories and transformed lives.'
                  : 'Boletín con actualizaciones, historias y vidas transformadas.'}
              </p>

              <form onSubmit={handleSubmit} className="space-y-4" data-testid="email-popup-form">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('common.emailPlaceholder', language)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                  disabled={loading}
                  data-testid="email-popup-input"
                />
                <input
                  type="tel"
                  placeholder={t('common.phonePlaceholder', language)}
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={loading}
                  data-testid="email-popup-phone"
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full px-6 py-3 bg-gradient-to-r from-sh-primary to-sh-bright text-white rounded-lg font-medium hover:shadow-lg transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="email-popup-submit"
                >
                  {loading ? t('popup.subscribing', language) : (
                    <>
                      {t('popup.subscribeNow', language)}
                      <ArrowRight size={18} />
                    </>
                  )}
                </button>
              </form>

              <p className="text-xs text-gray-500 mt-4 text-center">
                {language === 'en' ? 'We respect your privacy. Unsubscribe anytime.' : 'Respetamos tu privacidad. Cancela cuando quieras.'}
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default EmailPopup;
